import type {
  ShikiTransformer,
  HighlighterGeneric,
  BundledLanguage,
  BundledTheme,
} from 'shiki';

import type { ColorMode } from '@/constants';
import type {
  ServerLanguage,
  SingleAnnotation,
  MultipleAnnotations,
  HighlightRange,
} from './CodeSnippet.types';

export function generateHTMLFromCode({
  code,
  lang,
  highlighter,
  // By default, Shiki will generate both light and dark tokens. If we pass a specific color mode, Shiki will only apply one of them.
  lockedToColorMode = false,
  highlight = [],
  faded = [],
  highlightRange = [],
}: {
  code: string;
  lang: ServerLanguage;
  highlighter: HighlighterGeneric<BundledLanguage, BundledTheme>;
  lockedToColorMode?: false | ColorMode;
  highlight?: SingleAnnotation | MultipleAnnotations;
  faded?: SingleAnnotation | MultipleAnnotations;
  highlightRange?: HighlightRange[];
}) {
  const transformers = [createAnnotationTransformer(highlight)];

  if (faded.length) {
    transformers.push(createAnnotationTransformer(faded, 'faded'));
  }

  const decorations = highlightRange.map((range) => ({
    start: { line: range.line, character: range.start },
    end: { line: range.line, character: range.end },
    properties: { class: 'highlight-range' },
  }));

  return highlighter.codeToHtml(code, {
    // To save space, the light highlighter only includes JSX, not JS.
    lang: lang === 'js' ? 'jsx' : lang,
    themes: {
      light: 'josh-theme-light',
      dark: 'josh-theme-dark',
    },
    defaultColor: lockedToColorMode,
    transformers,
    decorations,
  });
}

export function createAnnotationTransformer(
  annotations: SingleAnnotation | MultipleAnnotations,
  className = 'highlighted'
) {
  // `annotations` might actually be a single annotation. Let's standardize it, by wrapping in an array if it's single.
  const normalizedAnnotations = getIsSingleAnnotation(annotations)
    ? [annotations]
    : annotations;

  const transformer: ShikiTransformer = {
    line(node, lineNum) {
      const isHighlighted = normalizedAnnotations.some(
        (annotation) => {
          return getIsCurrentLineAnnotated(annotation, lineNum);
        }
      );

      if (isHighlighted) {
        this.addClassToHast(node, className);
      }
    },
  };

  return transformer;
}

function getIsSingleAnnotation(
  annotation: SingleAnnotation | MultipleAnnotations | undefined
): annotation is SingleAnnotation {
  if (!annotation) {
    return false;
  }
  return annotation.every((val) => typeof val === 'number');
}

function getIsCurrentLineAnnotated(
  annotation: SingleAnnotation,
  lineNum: number
): boolean {
  const lineIndex = lineNum - 1;

  // Annotations can either be a single line ([5]) or multiline ([5, 10]). First check single annotations:
  if (annotation.length === 1 && annotation[0] === lineIndex) {
    return true;
  }

  if (
    annotation.length === 2 &&
    lineIndex >= annotation[0] &&
    lineIndex <= annotation[1]
  ) {
    return true;
  }

  return false;
}
